<template>
  <IconButton @click="profileModal?.$el?.present()" v-bind="$attrs" icon="icon-user" color="medium"></IconButton>
  <IonModal ref="profileModal">
    <IonHeader class="ion-no-border px-[10px]" color="white">
      <IonToolbar color="white" class="border-b-2 border-gray-300">
        <IonTitle color="medium"><i class="icon-user mr-2"></i>Profile</IonTitle>
        <IonButtons slot="end">
          <IconButton icon="icon-x" color="primary" @click="dismiss()"></IconButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent color="white">
      <div class="relative h-[150px] w-[150px] m-auto mt-6" @click.stop="profilePictureClicked">
        <div v-if="!auth.user?.profilePicUrl" class="profile-placeholder m-auto absolute">
          {{ initials }}
        </div>
        <div class="size-[150px] rounded-md overflow-hidden" v-else>
          <img class="object-cover size-full" :src="auth.user?.profilePicUrl" alt="Profile Photo" />
        </div>
        <i
          class="icon-camera bg-gray-500 rounded-full p-2 border-4 border-white -top-4 -right-4 absolute font-bold"
          @click.stop="profilePictureClicked"
        ></i>
      </div>
      <div class="text-gray-700 flex justify-center font-bold text-xl mt-4">{{ combinedName.join(' ') }}</div>
      <div class="text-gray-500 flex justify-center text-sm">{{ auth?.user?._id }}</div>
      <ListTitle title="edit profile" class="mt-4"></ListTitle>
      <IonList inset>
        <StackedTextInput
          title="First Name"
          v-model="firstNameControl.value"
          :errors="firstNameControl.errors"
          :state="firstNameControl.state"
          gray
          :clearable="false"
        ></StackedTextInput>
        <StackedTextInput
          title="Last Name"
          v-model="lastNameControl.value"
          :errors="lastNameControl.errors"
          :state="lastNameControl.state"
          class="text-gray-700"
          gray
          :clearable="false"
        ></StackedTextInput>
        <StackedTextInput
          title="User Name"
          :errors="usernameControl.errors"
          :state="usernameControl.state"
          v-model="usernameControl.value"
          type="email"
          gray
          :clearable="false"
        ></StackedTextInput>
        <StackedTextInput
          title="Phone Number"
          :state="'VALID'"
          v-model="phoneControl.value"
          type="tel"
          gray
          :clearable="false"
        ></StackedTextInput>
      </IonList>
      <SaveFormButtons
        @save="saveProfile"
        @cancel="cancelSaveProfile"
        :saving="savingProfile"
        :show="showSaveButtons"
        :disabled="!firstNameControl.valid || !lastNameControl.valid || !usernameControl.valid || !phoneControl.valid"
        cancel-color="white"
        class="px-4"
      ></SaveFormButtons>
      <SectionTitle class="mt-12 m-auto mx-4">Profile Actions</SectionTitle>
      <QuickActionButton
        @click="logout()"
        icon="icon-log-out"
        title="Logout"
        color="blue"
        subtitle="Log out of the NCSS mobile app"
      />
      <QuickActionButton
        @click="resetPassword"
        icon="icon-lock"
        title="Change Password"
        color="blue"
        subtitle="Update your password"
      />
      <QuickActionButton
        @click="requestAccountDelete"
        icon="icon-trash"
        title="Delete Account"
        color="red"
        subtitle="Request a permanent account deletion"
      />
    </IonContent>
  </IonModal>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>
<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import StackedTextInput from '@/components/forms/stacked/StackedTextInput.vue';
import ListTitle from '@/components/ListTitle.vue';
import { useFormControl } from '@/composables/use-form-control';
import {
  actionSheetController,
  alertController,
  IonButtons,
  IonContent,
  IonHeader,
  IonList,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
  modalController,
} from '@ionic/vue';
import { useAuth } from '@/stores/auth.store';
import { RequiredFieldValidator } from '@/composables/validators/required';
import SaveFormButtons from '@/components/forms/SaveFormButtons.vue';
import QuickActionButton from '@/components/forms/QuickActionButton.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import ResetPasswordModal from '@/components/user-account/ResetPasswordModal.vue';
import { UserService } from '@/http-services/user.service';
import { useCamera } from '@/composables/use-camera';
import { useToast } from '@/composables/use-toast';
import { PhoneNumberValidator } from '@/composables/validators/phonenumber';
import { EmojiValidator } from '@/composables/validators/emoji-validator';
import { UsernameOrEmailValidator } from '@/composables/validators/usernameoremail';
import IconButton from '@/components/forms/IconButton.vue';
import { NoSpecialCharactersValidator } from '@/composables/validators/no-special-characters-validator';
import { LettersOnlyValidator } from '@/composables/validators/letters-only-validator';
import { MaxLengthValidator } from '@/composables/validators/max-length';

const profileModal = useTemplateRef('profileModal');
const { getPhoto } = useCamera();
const { errorToast, successToast } = useToast();
const userService = new UserService();
const auth = useAuth();
const combinedName = computed(() => {
  return [
    (auth.user?.firstname || '').charAt(0).toUpperCase() + auth.user?.firstname.slice(1),
    (auth.user?.lastname || '').charAt(0).toUpperCase() + auth.user?.lastname.slice(1),
  ];
});

const firstNameControl = useFormControl(() => auth.user?.firstname, {
  validators: [RequiredFieldValidator(), EmojiValidator(), LettersOnlyValidator(), MaxLengthValidator(50)],
});
const lastNameControl = useFormControl(() => auth.user?.lastname, {
  validators: [RequiredFieldValidator(), EmojiValidator(), LettersOnlyValidator(), MaxLengthValidator(50)],
});
const usernameControl = useFormControl(() => auth.user?.username, {
  validators: [EmojiValidator(), NoSpecialCharactersValidator(), MaxLengthValidator(50)],
  asyncValidators: [UsernameOrEmailValidator(userService, auth.user?.username)],
});
const phoneControl = useFormControl(() => auth.user?.phone || '', {
  validators: [PhoneNumberValidator()],
});
const showSaveButtons = computed(() => {
  return firstNameControl.dirty || lastNameControl.dirty || usernameControl.dirty || phoneControl.dirty;
});
const initials = computed(() => {
  return combinedName.value
    .map((n) => n[0])
    .join('')
    .toUpperCase();
});
const savingProfile = ref(false);

async function saveProfile() {
  if (!usernameControl.valid || !firstNameControl.valid || !lastNameControl.valid || !phoneControl.valid) {
    await errorToast('Unable to save profile, there are invalid fields');
    return;
  }
  savingProfile.value = true;
  const response = await userService.updateProfile({
    firstname: firstNameControl.value,
    lastname: lastNameControl.value,
    username: usernameControl.value,
    phone: phoneControl.value,
  });

  if (!response.isSuccess) {
    await errorToast(response.message);
    savingProfile.value = false;
  } else {
    await successToast(response.message);
    auth.refresh();
  }
  savingProfile.value = false;
}

function dismiss() {
  cancelSaveProfile();
  return profileModal.value?.$el?.dismiss();
}

function cancelSaveProfile() {
  firstNameControl.reset();
  lastNameControl.reset();
  usernameControl.reset();
  phoneControl.reset();
}

async function profilePictureClicked() {
  if (auth.user?.profilePicUrl) {
    const a = await actionSheetController.create({
      buttons: [
        { role: 'cancel', text: 'Cancel' },
        { role: 'update', text: 'Update Photo' },
        { role: 'destructive', text: 'Remove Photo' },
      ],
    });
    await a.present();
    const { role } = await a.onDidDismiss();
    if (role === 'update') {
      await updateProfilePhoto();
    } else if (role === 'destructive') {
      await removeProfilePhoto();
    }
  } else {
    await updateProfilePhoto();
  }
}

async function updateProfilePhoto() {
  const { error, base64, errorMsg } = await getPhoto();
  if (!base64) {
    return;
  }
  if (error) {
    await errorToast(errorMsg ?? 'Error getting photo');
    return;
  }
  const response = await userService.updatePhoto(base64);

  if (!response.isSuccess) {
    await errorToast(response.message);
  } else {
    await successToast(response.message);
    auth.refresh();
  }
}

async function removeProfilePhoto() {
  const { success, error } = await userService.removePhoto();
  if (success) {
    successToast('Profile photo removed!');
    auth.refresh();
  } else {
    errorToast(error);
  }
}

async function requestAccountDelete() {
  const a = await alertController.create({
    header: 'Are you sure?',
    message: `Are you sure you want to delete your account? This action cannot be undone.`,
    buttons: [
      {
        role: 'cancel',
        text: 'Cancel',
      },
      {
        text: 'Delete',
        role: 'delete-account',
        cssClass: 'text-red-500',
      },
    ],
  });
  await a.present();
  const d = await a.onDidDismiss();
  if (d.role === 'delete-account' && auth.user) {
    const response = await userService.requestAccountDelete(auth.user._id, auth.user.phone, __APP_VERSION__);
    if (response.isSuccess) {
      successToast(response.message);
      logout();
    } else {
      errorToast(response.message);
    }
  }
}

async function resetPassword() {
  const modal = await modalController.create({
    component: ResetPasswordModal,
    componentProps: {
      includeCurrentPassword: true,
    },
  });
  await modal.present();
}

function logout() {
  return profileModal.value?.$el?.dismiss().then(() => auth.logout());
}
</script>

<style scoped>
.profile-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 150px;
  height: 150px;
  border-radius: 10%;
  background-color: #3e89bf;
  color: white;
  font-size: 60px;
  font-weight: bold;
}
</style>
